import React, { useEffect, useState } from "react";
import styles from "./Convention.module.sass";
import cn from "classnames";
import { toast } from "react-toastify";
import Card from "../../../../components/Card/index.js";
import Loader from "../../../../components/Loader/index.js";
import Actions from "../../../../components/Actions/index.js";
import { signConvention } from "../../../../services/signature.service.js";
import { downloadContract } from "../../../../services/contract.service.js";
import { sendConventionToOpco } from "../../../../services/opco.service.js";

const Convention = ({ className, contract, onContractUpdate, signatureStatus, folderId }) => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingInProgress, setSigningInProgress] = useState(false);

  /**
   * Gère le téléchargement de la convention
   */
  const handleDownloadConvention = async () => {
    if (!contract || !contract.convention) {
      toast.error("Aucune convention disponible pour ce contrat");
      return;
    }

    try {
      const success = await downloadContract({ ...contract, fileField: 'convention' });
      if (success) {
        toast.success("Convention téléchargée avec succès!");
      } else {
        toast.error("Erreur lors du téléchargement de la convention");
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors du téléchargement de la convention");
    }
  };

  const handleSendConventionOpco = async () => {
    if (!contract || !contract.convention) {
      toast.error("Aucune convention disponible pour ce contrat");
      return;
    }

    try {
      const success = await sendConventionToOpco(folderId);
      if (success) {
        toast.success("Convention envoyée avec succès!");
      } else {
        toast.error("Erreur lors de l'envoi de la convention");
      }
    } catch (error) {
      toast.error(error.message || "Erreur lors du téléchargement de la convention");
    }
  };

  const handleSignatureRequest = async () => {
    if (!contract || !contract.id) {
      toast.error("Impossible d'envoyer le document : contrat invalide");
      return;
    }
    
    if (!folderId) {
      toast.error("Impossible d'envoyer le document : identifiant du dossier manquant");
      return;
    }

    if (!contract.convention) {
      toast.error("Aucune convention disponible pour ce contrat");
      return;
    }

    try {
      setSigningInProgress(true);

      const company = contract.expand?.company_id || {};
      const center = contract.expand?.center_id || {};

      const missingEmails = [];

      if (!company.email && !company.contact_email) missingEmails.push("l'entreprise");
      if (!center.email && !center.contact_email) missingEmails.push("le centre de formation");

      if (missingEmails.length > 0) {
        toast.error(`Emails manquants pour ${missingEmails.join(', ')}`);
        return;
      }

      // Envoyer le document à la signature avec l'ID du dossier
      const result = await signConvention(folderId);

      // Mettre à jour le contrat localement avec l'ID de procédure
      if (onContractUpdate && result.procedure?.id) {
        onContractUpdate({
          ...contract,
          procedureConvention: result.procedure.id
        });
      }

      toast.success("Convention envoyée à la signature avec succès!");
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'envoi de la convention à la signature");
    } finally {
      setSigningInProgress(false);
    }
  };

  useEffect(() => {
    if (contract) {
      generateSignaturesList();
    }
  }, [contract, signatureStatus]);

  const generateSignaturesList = () => {
    if (!contract || !contract.expand) {
      setLoading(false);
      return;
    }

    const company = contract.expand.company_id || {};
    const center = contract.expand.center_id || {};

    // Récupérer les informations complètes des signataires
    const companySignatory = {
      firstName: company.director_first_name || company.contact_first_name || '',
      lastName: company.director_last_name || company.contact_last_name || '',
      fullName: company.director_name || company.contact_name ||
        (company.director_first_name && company.director_last_name ?
          `${company.director_first_name} ${company.director_last_name}` :
          (company.contact_first_name && company.contact_last_name ?
            `${company.contact_first_name} ${company.contact_last_name}` :
            `Représentant de ${company.name || 'l\'entreprise'}`)),
      email: company.director_email || company.email || '',
      role: company.director_role || company.contact_role || 'Responsable'
    };

    const centerSignatory = {
      firstName: center.director_first_name || center.contact_first_name || '',
      lastName: center.director_last_name || center.contact_last_name || '',
      fullName: center.director_name || center.contact_name ||
        (center.director_first_name && center.director_last_name ?
          `${center.director_first_name} ${center.director_last_name}` :
          (center.contact_first_name && center.contact_last_name ?
            `${center.contact_first_name} ${center.contact_last_name}` :
            `Représentant de ${center.name}`)),
      email: center.director_email || center.email || '',
      role: center.director_role || center.contact_role || 'Responsable formation'
    };

    // Récupérer les statuts depuis YouSign (nouvelle structure v3)
    const memberStatuses = {};
    if (signatureStatus?.signers) {
      signatureStatus.signers.forEach(signer => {
        if (signer.email) {
          memberStatuses[signer.email.toLowerCase()] = {
            status: signer.status,
            finishedAt: signer.signed_at,
            firstName: signer.first_name,
            lastName: signer.last_name
          };
        }
      });
    }

    // Créer la liste des signataires
    const signaturesList = [
      {
        ordre: 1,
        role: `${companySignatory.role} - Entreprise`,
        signataire: companySignatory.fullName,
        email: companySignatory.email,
        statut: memberStatuses[companySignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
          memberStatuses[companySignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
          memberStatuses[companySignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
          memberStatuses[companySignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[companySignatory.email.toLowerCase()]?.finishedAt ?
          new Date(memberStatuses[companySignatory.email.toLowerCase()]?.finishedAt).toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : ""
      },
      {
        ordre: 2,
        role: `${centerSignatory.role} - Centre de formation`,
        signataire: centerSignatory.fullName,
        email: centerSignatory.email,
        statut: memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'signed' ? "Signé" :
          memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'declined' ? "Refusé" : 
          memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'notified' ? "Notifié" :
          memberStatuses[centerSignatory.email.toLowerCase()]?.status === 'ready' ? "Prêt" : "En attente",
        date: memberStatuses[centerSignatory.email.toLowerCase()]?.finishedAt ?
          new Date(memberStatuses[centerSignatory.email.toLowerCase()]?.finishedAt).toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }) : ""
      }
    ];

    setSignatures(signaturesList);
    setLoading(false);
  };

  if (loading) {
    return (
      <Card
        className={cn(styles.card, className)}
        classCardHead={styles.head}
        title="Convention de formation"
        classTitle={cn("title-green", styles.title)}
      >
        <div className={styles.wrapper}>
          <Loader />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(styles.card, className)}
      classCardHead={styles.head}
      title="Convention de formation"
      classTitle={cn("title-green", styles.title)}
      head={
        <>
          {contract.convention && (
            <Actions
              className={styles.actions}
              items={[
                ...(!contract.procedureConvention ? [{
                  title: signingInProgress ? "Envoi en cours..." : "Envoyer à la signature",
                  action: handleSignatureRequest,
                  disabled: signingInProgress
                }] : []),
                {
                  title: "Télécharger Convention",
                  action: handleDownloadConvention
                },
                {
                  title: "Envoyer à l'opco",
                  action: handleSendConventionOpco
                }
              ]}
            />
          )}
        </>
      }
    >
      <div className={styles.wrapper}>
        {signatures.length > 0 ? (
          <div className={styles.table}>
            <div className={styles.row}>
              <div className={styles.col}>Ordre</div>
              <div className={styles.col}>Rôle</div>
              <div className={styles.col}>Signataire</div>
              <div className={styles.col}>Statut</div>
              <div className={styles.col}>Date</div>
            </div>
            {signatures.map((item, index) => (
              <div className={styles.row} key={index}>
                <div className={styles.col}>
                  {item.ordre}
                </div>
                <div className={styles.col}>
                  {item.role}
                </div>
                <div className={styles.col}>
                  <div className={styles.product}>{item.signataire}</div>
                  <div className={styles.invoice}>{item.email}</div>
                </div>
                <div className={styles.col}>
                  {item.statut === "Signé" && (
                    <div
                      className={cn(
                        "status-green-dark",
                        styles.status
                      )}
                    >
                      Signé
                    </div>
                  )}
                  {item.statut === "En attente" && (
                    <div
                      className={cn(
                        "status-gray-light",
                        styles.status
                      )}
                    >
                      En attente
                    </div>
                  )}
                  {item.statut === "Refusé" && (
                    <div
                      className={cn(
                        "status-red-dark",
                        styles.status
                      )}
                    >
                      Refusé
                    </div>
                  )}
                </div>
                <div className={styles.col}>
                  {item.date || "Non signé"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            Aucune information de signature disponible
          </div>
        )}
      </div>
    </Card>
  );
};

export default Convention;
